<script setup lang="ts">
import { useDisplay } from 'vuetify'
import { $getUserKopItems } from '@/api/kop'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { TableOptions } from '@/models/general/table'

interface IProps {
  maxItemsCount?: number,
}

const props = withDefaults(defineProps<IProps>(), {
  maxItemsCount: 0,
})

const vuetifyDisplay = useDisplay()

const itemTableHeaders = [
  { key: 'kop_item_code', title: 'KOP Item Code', sortable: false },
  { key: 'item_status_name', title: 'Status', sortable: false },
  { key: 'campaign_name', title: 'Project', sortable: false },
  { key: 'location_name', title: 'Location', sortable: false },
  { key: 'actions', title: '', sortable: false },
]

const itemTableOptions = ref<TableOptions>({
  itemsPerPage: 20,
  page: 1,
  sortBy: []
})

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
]

const isMounted = ref<boolean>(false)

const items = ref<any[]>([])
const itemsLoading = ref<boolean>(false)
const itemsCount = ref<number>(0)
const currentPage = ref(1)

const showKopItemDialog = ref(false)
const selectedKopItemId = ref(null)

const perPage = computed<number>(() => {
  return vuetifyDisplay?.lgAndUp ? 20 : 10
})

const tableHeight = computed<number | string>(() => {
  if (vuetifyDisplay?.lgAndUp) {
    if (props.maxItemsCount === 0) {
      return 100
    }
    return 500
  }
  return ''
})

const gerOrdering = () => {
  // Define default ordering
  let ordering = 'id'

  // Check if sortBy is defined and has at least one item
  if (itemTableOptions.value.sortBy.length > 0) {
    const sortBy = itemTableOptions.value.sortBy[0]
    const sortByKey = sortBy.key
    const sortOrder = sortBy.order === 'desc' ? '-' : '' // Determine the sorting order

    ordering = `${sortOrder}${sortByKey}`
  }

  return ordering
}

const getItems = async () => {
  itemsLoading.value = true

  const ordering = undefined

  try {
    const res = await $getUserKopItems({
      page: itemTableOptions.value.page,
      per_page: itemTableOptions.value.itemsPerPage,
      ordering: ordering,
    })

    items.value = res.data.results
    itemsCount.value = res.data.count
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e?.response?.status === 404) {
      itemTableOptions.value.page = 1
      await getItems()
    } else {
      console.log(e)
    }
  } finally {
    itemsLoading.value = false
  }
}

onMounted(async () => {
  await getItems()

  isMounted.value = true
})

// properties & methods are public
defineExpose({
  itemsCount,
})

const onUpdateTableOptions = async ({ page, itemsPerPage, sortBy }: TableOptions) => {
  itemTableOptions.value.page = page
  itemTableOptions.value.itemsPerPage = itemsPerPage
  itemTableOptions.value.sortBy = sortBy
  if (isMounted.value) {
    await getItems()
  }
}
</script>

<template>
  <VCard title="KOP Items Assigned To You">
    <VCardText>
      <VOverlay
        v-model="itemsLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>
      <VDataTableServer
        v-model:page="itemTableOptions.page"
        :items="[ ...items ]"
        :items-length="parseInt(String(itemsCount), 10)"
        :headers="itemTableHeaders"
        :loading="!!itemsLoading"
        :fixed-header="$vuetify.display.lgAndUp"
        :height="tableHeight"
        no-data-text="Items not found"
        density="compact"
        hover
        show-current-page
        :items-per-page-text="''"
        :items-per-page-options="itemsPerPageOptions"
        :items-per-page="itemTableOptions.itemsPerPage"
        @update:options="onUpdateTableOptions"
      >
        <template #item.actions="{ item }">
          <VBtn
            @click="selectedKopItemId = item.raw.id; showKopItemDialog = true"
          >
            View
          </VBtn>
        </template>
      </VDataTableServer>
    </VCardText>
    <KopItemDetailsModal
      v-model:kopItemDialogVisible="showKopItemDialog"
      :selected-kop-item-id="selectedKopItemId"
      :is-kop-item-list="true"
      @onUpdate="getItems"
    />
  </VCard>
</template>
